import { YeshumanMarkIcon } from '@/components/landing/yeshuman/yeshuman-brand-mark'
import {
  useOpenYeshumanContactSheet,
} from '@/components/landing/yeshuman/yeshuman-contact-sheet-provider'
import { YeshumanMarketingChrome } from '@/components/landing/yeshuman/yeshuman-marketing-chrome'
import {
  yeshumanAnn,
  yeshumanAnnDim,
  yeshumanDisplayHeading,
  yeshumanEditorial,
  yeshumanEditorialDisplay,
  yeshumanEditorialSm,
  yeshumanEditorialTerm,
  yeshumanHeroHeading,
  yeshumanMeasure,
  yeshumanMeasureNarrow,
  yeshumanTabular,
  yeshumanUiSans,
} from '@/components/landing/yeshuman/yeshuman-typography'
import {
  yeshumanBand,
  yeshumanBandAlt,
  yeshumanBandSoft,
  yeshumanContentColumn,
} from '@/components/landing/yeshuman/yeshuman-layout'
import { useRevealOnce } from '@/components/landing/yeshuman/use-reveal-once'
import { Button } from '@/components/ui/button'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { LandingCtaLink } from '@/landing/landing-cta-link'
import { resolveMarketingStickyNavChrome } from '@/landing/layouts/landing-layouts'
import { resolveLanding, type ClientWithLanding } from '@/landing/resolve-landing'
import { cn } from '@/lib/utils'
import { CURRENT_CLIENT } from '@/constants'

const sectionPad = 'py-[clamp(96px,9vw,132px)]'

/** Shared rough circle (logo mark) for stage glyphs. */
const STAGE_CIRCLE_D =
  'M58.5 17.26 C68.86 9.52, 82.87 6.66, 95.69 3.95 C108.5 1.24, 122.13 -1.09, 135.4 1 C148.67 3.1, 164.03 9.73, 175.28 16.53 C186.53 23.33, 194.22 31.81, 202.89 41.79 C211.56 51.77, 222.03 64.42, 227.29 76.41 C232.55 88.41, 234.57 100.76, 234.46 113.77 C234.36 126.79, 231.49 141.45, 226.65 154.49 C221.82 167.53, 214.11 181.4, 205.44 192.01 C196.77 202.62, 185.71 211.39, 174.66 218.18 C163.61 224.97, 151.71 230.32, 139.12 232.76 C126.53 235.21, 112.16 235.3, 99.14 232.83 C86.13 230.36, 72.97 224.89, 61.02 217.95 C49.06 211.02, 36.28 201.54, 27.42 191.22 C18.56 180.9, 12.23 168.45, 7.85 156.04 C3.48 143.64, 1.43 129.58, 1.18 116.78 C0.93 103.97, 2.34 91.51, 6.35 79.21 C10.37 66.92, 14.2 54.44, 25.27 43.02 C36.35 31.6, 62.5 17.01, 72.78 10.69 C83.07 4.37, 86.53 3.79, 86.97 5.1'

function YeshumanTalkCta({ className }: { className?: string }) {
  const openContactSheet = useOpenYeshumanContactSheet()
  return (
    <Button
      type="button"
      variant="marketingSolid"
      size="marketing"
      pressFeedback
      className={className}
      onClick={() => openContactSheet()}
    >
      Talk to us
    </Button>
  )
}

function YeshumanHero() {
  return (
    <section aria-labelledby="yeshuman-hero-heading" className={cn('yeshuman-hero-band', yeshumanBand)}>
      <div className={cn(yeshumanContentColumn, 'yeshuman-hero-copy')}>
        <h1 id="yeshuman-hero-heading" className={cn(yeshumanHeroHeading, 'yeshuman-hero-chunk')}>
          <span className="yeshuman-u-ship">Ship</span> the AI product your prototype promised.
        </h1>
        <p className={cn(yeshumanEditorial, yeshumanMeasure, 'yeshuman-hero-chunk mt-10')}>
          Yes Human is an AI-native web application with agent harnesses inside: opinionated
          starting points, plus places to customise. Prototyping is easy. Production is the harder
          set of decisions after people start using it.
        </p>
        <div className="yeshuman-hero-chunk mt-12 flex flex-wrap items-start gap-x-9 gap-y-5">
          <YeshumanTalkCta className={yeshumanUiSans} />
          <Button asChild variant="marketingLink" size="marketingLink" pressFeedback>
            <LandingCtaLink to="#build" className={cn(yeshumanAnn, 'mt-2.5')}>
              See what’s inside
              <span className="relative top-px ml-2" aria-hidden>
                ↓
              </span>
            </LandingCtaLink>
          </Button>
        </div>
      </div>
    </section>
  )
}

function YeshumanGapSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      id="gap"
      tabIndex={-1}
      aria-labelledby="yeshuman-gap-heading"
      className={cn(sectionPad, yeshumanBandAlt, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>Prototype to production</div>
        <h2 id="yeshuman-gap-heading" className={cn(yeshumanDisplayHeading, 'max-w-[18em]')}>
          The prototype is useful. Production is a different job.
        </h2>
        <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7 yeshuman-reveal')}>
          Prototyping is exceedingly easy. You can stand something up that looks right. That is
          worth doing. A prototype still has to earn its place: people have to use it. Production is
          the harder set of decisions after that. If you already have a post-Lovable prototype, this
          is the next stretch.
        </p>
        <div className="mt-14 grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              label: 'First',
              term: 'The prototype',
              desc: 'Easy to stand up. It has to get users. That is how it earns a place.',
            },
            {
              label: 'Then',
              term: 'Production',
              desc: 'DevOps, infrastructure, scale, privacy. The operational decisions a demo never had to make.',
            },
            {
              label: 'Once people use it',
              term: 'Listening',
              desc: 'Feedback, usability, what to build next. Use can go into a backlog instead of another guess.',
            },
          ].map((item) => (
            <div key={item.term} className="yeshuman-reveal">
              <span className={cn('mb-2 block', yeshumanAnn, 'text-[var(--marketing-gold)]')}>
                {item.label}
              </span>
              <span className={cn('mb-1.5 block', yeshumanEditorialTerm)}>{item.term}</span>
              <p className={cn(yeshumanEditorialSm, 'max-w-[40ch]')}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function YeshumanBuildSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  const shapes = [
    {
      label: '01 · Connectors',
      term: 'Talk to the systems you already run',
      desc: 'Usually MCP. Also APIs, client adapters, and CLIs on sandboxes. You do not have to leave your tools.',
    },
    {
      label: '02 · Harnesses',
      term: 'Runtimes we shipped',
      desc: 'A harness is one runtime: tool calling, memory, the agent loop. Not the product. Not the page. The stack has several.',
    },
    {
      label: '03 · Surfaces',
      term: 'UIs, voice, phone, iOS',
      desc: 'Where people actually use it. An app they sign into, a phone line, a voice session, or a panel in software they already have open.',
    },
    {
      label: '04 · Memory',
      term: 'What people tell it stays',
      desc: 'In the main user-facing harness, memory is agent memory about a user. Onboarding, preferences, feedback. That can be queried later.',
    },
  ]

  return (
    <section
      ref={ref}
      id="build"
      tabIndex={-1}
      aria-labelledby="yeshuman-build-heading"
      className={cn(sectionPad, yeshumanBand, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>What’s inside</div>
        <h2 id="yeshuman-build-heading" className={cn(yeshumanDisplayHeading, 'max-w-[20em]')}>
          Harnesses inside an application you can shape.
        </h2>
        <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
          We call the place a workbench: several harnesses, plus tools for people and agents that
          are not themselves a harness. A rubric builder. A prompt builder. A listening-scribe tool.
        </p>

        <div className="mt-16 grid gap-5 md:grid-cols-2">
          {shapes.map((shape) => (
            <article
              key={shape.label}
              className={cn(
                'yeshuman-build-card yeshuman-reveal border border-[var(--marketing-border)] bg-[var(--marketing-bg)] px-7 py-8 transition-colors duration-200'
              )}
            >
              <div className={cn('mb-3', yeshumanAnn, 'text-[var(--marketing-gold)]')}>{shape.label}</div>
              <h3 className={cn(yeshumanEditorialTerm, 'mb-2')}>{shape.term}</h3>
              <p className={cn(yeshumanEditorialSm, yeshumanMeasureNarrow)}>{shape.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function YeshumanPlatformSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      id="platform"
      tabIndex={-1}
      aria-labelledby="yeshuman-platform-heading"
      className={cn(
        sectionPad,
        yeshumanBandSoft,
        'border-y border-[var(--marketing-border)]',
        anchorClass,
        revealed && 'yeshuman-deliver-on'
      )}
    >
      <div className={yeshumanContentColumn}>
        <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>The platform</div>
        <h2 id="yeshuman-platform-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
          You don’t start from scratch.
        </h2>
        <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
          Opinionated starting points, plus places to customise. Connectors come first: the product
          talks to systems you already run. You shape the surfaces people use.
        </p>

        <div className="mt-20 grid items-center gap-14 md:grid-cols-[minmax(0,460px)_1fr]">
          <div>
            <div className={cn('mb-5', yeshumanAnn)}>
              <span className="text-[var(--marketing-gold)]">Fig. 01</span>
              &nbsp;&nbsp;
              <span className={yeshumanAnnDim}>What’s already there, top to bottom</span>
            </div>
            <svg
              className={cn('yeshuman-draw block h-auto w-full', revealed && 'yeshuman-draw-on')}
              viewBox="0 0 520 640"
              role="img"
              aria-label="Diagram of the five layers: surface, harness, memory, evaluation, connectors"
            >
              <line pathLength={1} x1="260" y1="0" x2="260" y2="640" />
              <polygon pathLength={1} points="60,540 260,485 460,540 260,595" />
              <polygon pathLength={1} points="60,440 260,385 460,440 260,495" className="yeshuman-d2" />
              <polygon pathLength={1} points="60,340 260,285 460,340 260,395" className="yeshuman-d2" />
              <polygon pathLength={1} points="60,240 260,185 460,240 260,295" className="yeshuman-d3" />
              <polygon
                pathLength={1}
                points="60,140 260,85 460,140 260,195"
                className="yeshuman-d3 yeshuman-dash"
              />
              <line pathLength={1} x1="60" y1="120" x2="60" y2="560" className="yeshuman-d4 yeshuman-dash" />
              <line pathLength={1} x1="460" y1="120" x2="460" y2="560" className="yeshuman-d4 yeshuman-dash" />
              {[85, 185, 285, 385, 485].map((y) => (
                <line
                  key={y}
                  pathLength={1}
                  x1="252"
                  y1={y}
                  x2="268"
                  y2={y}
                  className="yeshuman-d4"
                  style={{ stroke: 'var(--marketing-text)' }}
                />
              ))}
            </svg>
            <div className={cn('mt-5', yeshumanAnn, yeshumanAnnDim)}>
              The dashed layer on top is the part we shape with you.
            </div>
          </div>
          <ol className="list-none flex flex-col gap-6">
            {[
              {
                term: 'Connectors',
                desc: 'Usually MCP. Also APIs, client adapters, and CLIs on sandboxes.',
              },
              {
                term: 'Surface',
                badge: 'Yours to shape',
                desc: 'UIs, voice, phone, iOS. The app, a phone line, or a panel in tools they already have open.',
              },
              {
                term: 'Harness',
                desc: 'One runtime: tool calling, memory, the agent loop. The stack has several.',
              },
              {
                term: 'Memory',
                desc: 'Agent memory about a user. What people tell it stays, so later work can use it.',
              },
              {
                term: 'Evaluation',
                desc: 'Scores come from rubric and methodology layers, not raw model analysis. That is the judgment. The process itself can be evaluated.',
              },
            ].map((item) => (
              <li key={item.term} className="yeshuman-callout">
                <span className="mb-1 flex flex-wrap items-baseline gap-x-3">
                  <span className={yeshumanEditorialTerm}>{item.term}</span>
                  {'badge' in item && item.badge ? (
                    <span className={cn(yeshumanAnn, 'text-[var(--marketing-gold)]')}>{item.badge}</span>
                  ) : null}
                </span>
                <span className={cn(yeshumanEditorialSm, 'block')}>{item.desc}</span>
              </li>
            ))}
          </ol>
        </div>
        <p className={cn(yeshumanEditorialSm, yeshumanMeasure, 'mt-12')}>
          A shipped slice of the software factory: agent-assisted automations that collect
          information and feedback and put it into backlogs.
        </p>
        <p className={cn('mt-6', yeshumanEditorialSm)}>
          <LandingCtaLink to="/concepts/topology" className={yeshumanAnn}>
            Topology
          </LandingCtaLink>
          <span className={yeshumanAnnDim}>. The system diagram.</span>
        </p>
      </div>
    </section>
  )
}

function StageGlyph({ n, drawOn }: { n: string; drawOn: boolean }) {
  return (
    <div className="relative mb-5 size-[4.5rem]">
      <svg
        className={cn('yeshuman-draw absolute inset-0 size-full', drawOn && 'yeshuman-draw-on')}
        viewBox="0 0 253 253"
        aria-hidden
      >
        <path
          pathLength={1}
          d={STAGE_CIRCLE_D}
          transform="translate(9 9)"
          style={{ stroke: 'var(--marketing-gold)' }}
        />
      </svg>
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center text-[0.9375rem] tracking-[0.08em]',
          yeshumanTabular,
          'text-[var(--marketing-text)]'
        )}
      >
        {n}
      </span>
    </div>
  )
}

function YeshumanLandsSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      id="lands"
      tabIndex={-1}
      aria-labelledby="yeshuman-lands-heading"
      className={cn(sectionPad, yeshumanBandAlt, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>How it lands</div>
        <h2 id="yeshuman-lands-heading" className={cn(yeshumanDisplayHeading, 'max-w-[18em]')}>
          Start small. Stay for production.
        </h2>
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              n: '01',
              term: 'Working session',
              desc: 'We make a shared picture of the system: what it is, what is in and out, and the harder production decisions. You leave with that picture. You can keep working from it.',
              meta: 'An hour together',
            },
            {
              n: '02',
              term: 'First build',
              desc: 'Your data and the way you already work, on software that’s already running. We keep the first slice small.',
              meta: 'A first slice people can use',
            },
            {
              n: '03',
              term: 'Live, then the next bit',
              desc: 'Real people using it. Feedback can go into a backlog. We build what comes next from that.',
              meta: 'As long as it’s earning its keep',
            },
          ].map((stage) => (
            <div key={stage.n} className="yeshuman-reveal">
              <StageGlyph n={stage.n} drawOn={revealed} />
              <span className={cn('mb-1.5 block', yeshumanEditorialTerm)}>{stage.term}</span>
              <p className={cn(yeshumanEditorialSm, 'mb-3 max-w-[40ch]')}>{stage.desc}</p>
              <span className={cn(yeshumanAnn, yeshumanAnnDim)}>{stage.meta}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function YeshumanYoursSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      id="yours"
      tabIndex={-1}
      aria-labelledby="yeshuman-yours-heading"
      className={cn(sectionPad, yeshumanBand, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className="grid items-start gap-14 md:grid-cols-[minmax(0,18rem)_1fr]">
          <div className="yeshuman-reveal">
            <h2 id="yeshuman-yours-heading" className={yeshumanDisplayHeading}>
              What stays yours.
            </h2>
            <p className={cn(yeshumanEditorial, 'mt-6 max-w-[34ch]')}>
              We help you figure out the AI part. Then we build it with you.
            </p>
            <p className={cn(yeshumanEditorialSm, 'mt-5 max-w-[34ch] text-[var(--marketing-text)]')}>
              You pick the model, keep your way of working, and keep the controls you have to meet.
            </p>
          </div>
          <div className="grid gap-x-8 gap-y-9 sm:grid-cols-2">
            {[
              {
                label: 'Your environment',
                desc: 'Runs on our servers or yours.',
              },
              {
                label: 'Your data',
                desc: 'Stays in your account. We don’t use it to train anyone else’s model.',
              },
              {
                label: 'Your systems',
                desc: 'Hooks into what you already use.',
              },
              {
                label: 'Your team',
                desc: 'Daryl and Ada. If you want to run it yourself later, we hand it over properly.',
              },
            ].map((item) => (
              <div key={item.label} className="yeshuman-reveal">
                <span className={cn('mb-2 block', yeshumanAnn)}>{item.label}</span>
                <p className={yeshumanEditorialSm}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function YeshumanTechSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      id="tech"
      tabIndex={-1}
      aria-labelledby="yeshuman-tech-heading"
      className={cn(sectionPad, yeshumanBandSoft, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>Technology choices</div>
        <h2 id="yeshuman-tech-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
          Django, LangGraph, and UI.
        </h2>
        <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
          Opinionated Django starting points, plus places to customise. A harness is one LangGraph
          runtime we shipped. UI is the React and Vite surface. The stack has several harnesses,
          not one.
        </p>
      </div>
    </section>
  )
}

function YeshumanTeamSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  const people = [
    {
      name: 'Daryl Antony',
      role: 'Co-founder',
      bio: 'When GPT-3 arrived, it finally matched how I think: words, meaning, ideas that can evolve in public. I started Yes Human to close the gap between a prototype that wins a room and production software people rely on. I wanted to be a great engineer again. This platform is what those two years of experiments became.',
    },
    {
      name: 'Ada Robotson',
      role: 'Co-founder, product and UX',
      bio: 'With AI, it felt like I had all the power in the world: I could ship without waiting for anyone. Then reality landed. Making something and getting it adopted are two different jobs. I joined Yes Human to work back from the problem and turn ideas into agents and products for people to use.',
    },
  ]

  return (
    <section
      ref={ref}
      id="team"
      tabIndex={-1}
      aria-labelledby="yeshuman-team-heading"
      className={cn(sectionPad, yeshumanBandAlt, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className={cn('mb-7', yeshumanAnn, yeshumanAnnDim)}>The people</div>
        <h2 id="yeshuman-team-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
          You’ll be working with us.
        </h2>
        <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
          Daryl and Ada are in the session. We’ll add people here as they join.
        </p>
        <div className="mt-16 grid gap-10 md:grid-cols-2">
          {people.map((person) => (
            <article key={person.name} className="yeshuman-reveal">
              <div className="mb-5 flex items-center gap-4">
                <YeshumanMarkIcon className="size-16 shrink-0" />
                <div>
                  <span className={cn('block', yeshumanEditorialTerm)}>{person.name}</span>
                  <span className={cn('mt-1 block', yeshumanAnn, yeshumanAnnDim)}>{person.role}</span>
                </div>
              </div>
              <p className={cn(yeshumanEditorialSm, yeshumanMeasureNarrow)}>{person.bio}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function YeshumanStripSection() {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      aria-labelledby="yeshuman-strip-quote"
      className={cn(yeshumanBand, revealed && 'yeshuman-reveal-on')}
    >
      <div className={cn(yeshumanContentColumn, 'py-[112px] text-center')}>
        <p
          id="yeshuman-strip-quote"
          className={cn(yeshumanEditorialDisplay, 'yeshuman-reveal mx-auto max-w-[22em]')}
        >
          Toward intelligence-first organisations.
        </p>
        <p className={cn(yeshumanEditorial, 'yeshuman-reveal mx-auto mt-6 max-w-[28em]')}>
          People and agents, in the same organisation. Intelligence you own.
        </p>
      </div>
    </section>
  )
}

function YeshumanFaqSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  const items = [
    {
      q: 'Is this a chatbot?',
      a: (
        <>
          Chat can be one way in. What we usually ship is an application with harnesses inside:
          connectors to your tools, memory about the people using it, and an evaluation pipeline
          with rubrics. If you only need a FAQ bot, you don’t need us.
        </>
      ),
    },
    {
      q: 'What’s the difference between a prototype and production?',
      a: (
        <>
          Prototyping is easy. A prototype has to get users. Production is the harder set of
          decisions after that: infrastructure, scale, privacy, listening, feedback, usability.
        </>
      ),
    },
    {
      q: 'What is a harness?',
      a: (
        <>
          One LangGraph runtime we shipped: tool calling, memory, the agent loop. Not the whole
          product. Not the page. The stack has several.
        </>
      ),
    },
    {
      q: 'How is this different from a custom build?',
      a: (
        <>
          You don’t start from scratch. Opinionated Django starting points, plus places to
          customise. The foundation already runs.
        </>
      ),
    },
    {
      q: 'What happens to our data?',
      a: (
        <>
          It stays in your account. Our hosting or yours. We don’t train someone else’s model on it.
        </>
      ),
    },
    {
      q: 'Can a person overrule what it decides?',
      a: (
        <>
          Yes. Scores come from rubric and methodology layers, not raw model analysis. A person can
          override.
        </>
      ),
    },
    {
      q: 'Do we have to leave our current tools?',
      a: (
        <>
          No. Connectors attach to what you already run. Usually MCP. Also APIs and adapters.
          Sometimes that’s its own app. Sometimes a panel inside tools your team already has open.
        </>
      ),
    },
    {
      q: 'Who builds this?',
      a: (
        <>
          Daryl and Ada. They’re the people in the session.
        </>
      ),
    },
    {
      q: 'Who is this for?',
      a: (
        <>
          Teams with a real product to get live. Clear users, clear work, and a first slice worth
          putting in front of people. Fine if you’re coming from a prototype that already has users.
        </>
      ),
    },
    {
      q: 'What is the working session?',
      a: (
        <>
          We look at what you have and what it should become. You leave with a shared picture of
          the system: what is in and out, and the harder production decisions. You can keep working
          from it, either way.
        </>
      ),
    },
  ]

  return (
    <section
      ref={ref}
      id="faq"
      tabIndex={-1}
      aria-labelledby="yeshuman-faq-heading"
      className={cn(sectionPad, yeshumanBandAlt, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <h2 id="yeshuman-faq-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
          Questions.
        </h2>
        <dl className="mt-14 flex flex-col gap-10">
          {items.map((item) => (
            <div key={item.q} className="yeshuman-reveal max-w-[65ch]">
              <dt className={cn('mb-2', yeshumanEditorialTerm)}>{item.q}</dt>
              <dd className={yeshumanEditorialSm}>{item.a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  )
}

function YeshumanClosingSection({ anchorClass }: { anchorClass: string }) {
  const { ref, revealed } = useRevealOnce<HTMLElement>()

  return (
    <section
      ref={ref}
      id="talk"
      tabIndex={-1}
      aria-labelledby="yeshuman-closing-heading"
      className={cn(sectionPad, yeshumanBand, anchorClass, revealed && 'yeshuman-reveal-on')}
    >
      <div className={yeshumanContentColumn}>
        <div className="grid items-start gap-16 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <div>
            <h2 id="yeshuman-closing-heading" className={cn(yeshumanDisplayHeading, 'max-w-[16em]')}>
              Book a working session.
            </h2>
            <p className={cn(yeshumanEditorial, yeshumanMeasure, 'mt-7')}>
              An hour on what you have now, what it can become, and how it gets live. We’ll draw it
              with you.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <YeshumanTalkCta className={yeshumanUiSans} />
              <span className={cn(yeshumanEditorialSm, yeshumanAnnDim)}>Opens a short form.</span>
            </div>
          </div>
          <div className="yeshuman-reveal border border-[var(--marketing-border)] bg-[var(--yeshuman-paper-alt)] px-7 py-8">
            <span className={cn('mb-6 block', yeshumanAnn, yeshumanAnnDim)}>What happens in the hour</span>
            <ol className="flex list-none flex-col gap-5">
              {[
                {
                  t: '0–15',
                  lead: 'What exists.',
                  body: 'Your prototype, your data, the decision it’s meant to make.',
                },
                {
                  t: '15–40',
                  lead: 'We draw it.',
                  body: 'Where people use it, what the agent does, what it has to remember.',
                },
                {
                  t: '40–60',
                  lead: 'The first build.',
                  body: 'What’s small enough to ship first, and what it would take.',
                },
              ].map((row) => (
                <li key={row.t} className="grid grid-cols-[3.75rem_1fr] gap-3">
                  <span
                    className={cn(
                      'pt-0.5 text-[0.75rem] tracking-[0.14em]',
                      yeshumanTabular,
                      'text-[var(--marketing-gold)]'
                    )}
                  >
                    {row.t}
                  </span>
                  <span className={yeshumanEditorialSm}>
                    <span className="text-[var(--marketing-text)]">{row.lead}</span> {row.body}
                  </span>
                </li>
              ))}
            </ol>
            <p className={cn(yeshumanEditorialSm, 'mt-7')}>
              You leave with that picture of the system, whether or not we work together.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export function YeshumanWorkbenchLanding() {
  const { guestTheme } = useGuestSurfaceTheme()
  const client = CURRENT_CLIENT as ClientWithLanding
  const { chrome } = resolveLanding(client, guestTheme)
  const { scrollMt: sectionAnchor } = resolveMarketingStickyNavChrome(chrome.headerStyle)

  return (
    <YeshumanMarketingChrome>
      <main id="main" tabIndex={-1} className="outline-none">
        <YeshumanHero />
        <YeshumanGapSection anchorClass={sectionAnchor} />
        <YeshumanBuildSection anchorClass={sectionAnchor} />
        <YeshumanPlatformSection anchorClass={sectionAnchor} />
        <YeshumanLandsSection anchorClass={sectionAnchor} />
        <YeshumanYoursSection anchorClass={sectionAnchor} />
        <YeshumanTechSection anchorClass={sectionAnchor} />
        <YeshumanTeamSection anchorClass={sectionAnchor} />
        <YeshumanStripSection />
        <YeshumanFaqSection anchorClass={sectionAnchor} />
        <YeshumanClosingSection anchorClass={sectionAnchor} />
      </main>
    </YeshumanMarketingChrome>
  )
}
